// src/components/TeamPage.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import NavLink from './common/NavLink';
import { Helmet } from 'react-helmet-async';
import { slugify } from '../utils/slugify'; 
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Button,
  Grid,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  List,           
  ListItem,       
  ListItemText,
  Tooltip,
} from '@mui/material';
import TwitterIcon from '@mui/icons-material/Twitter';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SouthIcon from '@mui/icons-material/South';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RequireVip from './RequireVip'
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const formatTeamValue = (valueInMillions) => {
  const value = Number(valueInMillions);
  if (!Number.isFinite(value)) return null;
  const formatter = new Intl.NumberFormat('en-US', {
    maximumSignificantDigits: 2,
    useGrouping: false,
  });
  return value >= 1
    ? `${formatter.format(value)}M €`
    : `${formatter.format(value * 1000)}k €`;
};

const TeamPage = () => {
  const { teamId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLeagueIndex, setSelectedLeagueIndex] = useState(0);
  const [leagueMenuAnchor, setLeagueMenuAnchor] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/team/${teamId}/dashboard`)
      .then((res) => {
        setData(res.data);
		setSelectedLeagueIndex(0); // resetuj wybór ligi na domyślną
        setLoading(false);
      })
      .catch((err) => {
        console.error('Błąd pobierania danych drużyny:', err);
        setLoading(false);
      });
  }, [teamId]);

  useEffect(() => {
    const leagues = data?.leagues || [];
    if (!leagues.length) return;

    const leagueWithChancesIndex = leagues.findIndex((league) => (
      Object.keys(league.chances || {}).length > 0
    ));

    if (leagueWithChancesIndex >= 0) {
      setSelectedLeagueIndex(leagueWithChancesIndex);
    }
  }, [data]);

  if (loading) return <LinearProgress sx={{ mt: 1 }} />;
  
  if (!data) {
  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h5" color="text.secondary" gutterBottom>
        Strony drużyn dostępne wyłącznie dla klubów grających w polskich ligach
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
        Jeśli uważasz, że to błąd – napisz do nas na kontakt@symuliga.pl
      </Typography>
    </Box>
  );
}

  const { team, recentMatches, upcomingMatches, leagues = [], achievements = [] } = data;

  const currentLeague = leagues[selectedLeagueIndex] || {};
  const leagueTable = currentLeague.leagueTable || [];
  const competition = currentLeague.competition || {};
  const chances = currentLeague.chances || {};
  const promotionRules = currentLeague.promotionRules || {};
const positionProbabilities = currentLeague.positionProbabilities || [];

const teamName = team?.name || 'Drużyna';
const regionName = team?.region_name || '';
const leagueName = competition?.full_name || '';
const getCompetitionLabel = (competition = {}) => {
  const name = competition.full_name || '';
  return competition.season ? `${name} (${competition.season})` : name;
};
const getCompetitionUrl = (competition = {}) => (
  `/liga/${competition.competition_id}/${slugify(competition.full_name || competition.name || '')}`
);
const getRuleName = (key, fallback) => {
  const value = promotionRules?.[key];
  return value && String(value).trim() ? value : fallback;
};

const pageTitle = `${teamName} - Tabela, terminarz, wyniki | SymuLiga`;
const pageDesc = `Statystyki i symulacje dla klubu ${teamName} (${regionName}). Sprawdź tabelę, ranking ELO, szanse na awans w lidze ${leagueName} oraz ostatnie mecze.`;

  return (
  <>
    <Box sx={{ maxWidth: 1300, mx: 'auto', px: { xs: 0, sm: 1, md: 2 }, py: 0 }}>
	<Helmet>
        <title>{pageTitle}</title>
		<link rel="canonical" href={`https://symuliga.pl/team/${teamId}`} />
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={`${teamName} - tabela, terminarz, wyniki`} />
        <meta property="og:description" content={`Jakie szanse na awans/spadek ma ${teamName} w lidze ${leagueName}? Sprawdź symulacje i przewidywania.`} />
    </Helmet>
      {/* Nagłówek drużyny */}
      <Box sx={{ textAlign: 'center', pt: 2, pb: 3, borderBottom: '1px solid #333', mt: -1, backgroundColor: 'background.default' }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.2rem', sm: '3.1rem', md: '3.5rem' }, color: '#e8e8e8', lineHeight: 1.05 }}>
          {team.name}
        </Typography>

        {team.x_name && (
          <Button component="a" href={`https://twitter.com/${team.x_name.replace('@', '')}`} target="_blank" startIcon={<TwitterIcon sx={{ fontSize: 16 }} />} size="small" variant="text" sx={{ color: '#1da1f2', fontSize: '0.875rem', mb: 0 }}>
            {team.x_name}
          </Button>
        )}
		
		

        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" pt='10px' flexWrap="wrap" useFlexGap sx={{ '& > *': { minWidth: 78 } }}>
		
		{/* ELO */}
          <Box textAlign="center">
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Elo (pkt)</Typography>
            <RequireVip variant='short-text' requirement={team.statsRequirement}>
				<Typography fontWeight={700} color="#00bcd4" sx={{ fontSize: '0.95rem' }}>{team.elo_ranking}</Typography>
			</RequireVip>
          </Box>
          {team.true_value != null && (
            <Box textAlign="center">
              <Stack direction="row" spacing={0.4} justifyContent="center" alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Wartość drużyny
                </Typography>
                <Tooltip title="Wartość z Transfermarkt skorygowana o wiek zawodników" arrow>
                  <InfoOutlinedIcon
                    aria-label="Informacja o wartości drużyny"
                    sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </Stack>
              <Typography fontWeight={700} color="#ffb74d" sx={{ fontSize: '0.95rem' }}>
                {formatTeamValue(team.true_value)}
              </Typography>
            </Box>
          )}
          {/* 2. Województwo + ranking regionalny – klikalne */}
          <Box textAlign="center">
            <Typography  display='block' variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Województwo (Ranking)</Typography>
            <Typography
              component={NavLink}
              to={`/elo-ranking?region=${team.region_id}`}
              sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#ccc', cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {team.region_name}             
			  <RequireVip variant='nothing' requirement={team.statsRequirement}>
					(#{team.elo_position_region || '—'})			
			  </RequireVip>

            </Typography>
          </Box>

          

          {/* 3. Ranking ELO PL – klikalny */}
          {team.elo_position_poland && (
            <Box textAlign="center">
              <Typography display='block' variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Ranking Elo (PL)</Typography>
              
			  <RequireVip variant='short-text' requirement={team.statsRequirement}>
				  <Typography
					component={NavLink}
					to="/elo-ranking"
					sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#66bb6a', cursor: 'pointer'}}
				  >
					#{team.elo_position_poland}
				  </Typography>
			  </RequireVip>
            </Box>
          )}

          {/* 1. Liga – dropdown tylko jeśli >1 liga */}
          {leagues.length > 1 ? (
            <Box textAlign="center">
              <Typography display="block" variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Liga</Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 180, border: '1px solid rgba(255,255,255,0.23)', borderRadius: 1, height: { xs: 35, sm: 52 }, px: 1 }}>
                <Typography
                  component={NavLink}
                  to={getCompetitionUrl(competition)}
                  sx={{ color: '#00bcd4', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.9rem' }, textDecoration: 'none', lineHeight: 1.2, flex: 1, '&:hover': { textDecoration: 'underline' } }}
                >
                  {getCompetitionLabel(competition)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(event) => setLeagueMenuAnchor(event.currentTarget)}
                  sx={{ color: '#00bcd4', ml: 0.5, p: 0.25 }}
                  aria-label="Wybierz ligę"
                >
                  <ArrowDropDownIcon />
                </IconButton>
              </Box>
              <Menu
                anchorEl={leagueMenuAnchor}
                open={Boolean(leagueMenuAnchor)}
                onClose={() => setLeagueMenuAnchor(null)}
              >
                {leagues.map((league, index) => (
                  <MenuItem
                    key={league.competition.competition_id}
                    selected={index === selectedLeagueIndex}
                    onClick={() => {
                      setSelectedLeagueIndex(index);
                      setLeagueMenuAnchor(null);
                    }}
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' }, padding: '4px 8px', minHeight: '24px' }}
                  >
                    {getCompetitionLabel(league.competition)}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          ) : (
            <Box textAlign="center">
              <Typography display='block' variant="caption" color="text.secondary" 
			   sx={{ fontSize: '0.7rem' }}>Liga</Typography>
              <Typography fontWeight={700} color="#00bcd4" 
			  component={NavLink}
              to={getCompetitionUrl(currentLeague.competition)}
			  sx={{ fontSize: '0.95rem' }}>
                {getCompetitionLabel(currentLeague.competition)}
              </Typography>
            </Box>
          )}
        </Stack>


	

			{/* 4. Szanse – klikalne do summary */}
			
			{Object.keys(chances).length > 0 && (
			<RequireVip variant='nothing' requirement={team.statsRequirement}>
			  <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" sx={{ mt: 2 }}>
				{chances.promotion_direct > 0.1 && (
				  <Box textAlign="center" component={NavLink} to={`/liga/${currentLeague.competition_id}/summary`} sx={{ cursor: 'pointer' }}>
					<EmojiEventsIcon sx={{ color: '#ffd700', fontSize: 21 }} />
					<Typography color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.2 }}>{getRuleName('promotion_spots_name', 'Awans')}</Typography>
					<Typography fontWeight={900} color="#ffd700" sx={{ fontSize: '1rem' }}>{chances.promotion_direct.toFixed(1)}%</Typography>
				  </Box>
				)}
				{chances.promotion_playoff > 0.1 && (
				  <Box textAlign="center" component={NavLink} to={`/liga/${currentLeague.competition_id}/summary`} sx={{ cursor: 'pointer' }}>
					<TrendingUpIcon sx={{ color: '#00e676', fontSize: 21 }} />
					<Typography color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.2 }}>{getRuleName('promotion_playoff_spots_name', 'Baraże o awans')}</Typography>
					<Typography fontWeight={900} color="#00e676" sx={{ fontSize: '1rem' }}>{chances.promotion_playoff.toFixed(1)}%</Typography>
				  </Box>
				)}
				{chances.relegation_playoff > 0.1 && (
				  <Box textAlign="center" component={NavLink} to={`/liga/${currentLeague.competition_id}/summary`} sx={{ cursor: 'pointer' }}>
					<SouthIcon sx={{ color: '#ff5722', fontSize: 21 }} />
					<Typography color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.2 }}>{getRuleName('relegation_playoff_spots_name', 'Baraże o utrzymanie')}</Typography>
					<Typography fontWeight={900} color="#ff5722" sx={{ fontSize: '1rem' }}>{chances.relegation_playoff.toFixed(1)}%</Typography>
				  </Box>
				)}
				{chances.relegation_direct > 0.1 && (
				  <Box textAlign="center" component={NavLink} to={`/liga/${currentLeague.competition_id}/summary`} sx={{ cursor: 'pointer' }}>
					<SouthIcon sx={{ color: '#d50000', fontSize: 21 }} />
					<Typography color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.2 }}>{getRuleName('relegation_spots_name', 'Spadek')}</Typography>
					<Typography fontWeight={900} color="#d50000" sx={{ fontSize: '1rem' }}>{chances.relegation_direct.toFixed(1)}%</Typography>
				  </Box>
				)}
			  </Stack>
			 </RequireVip>
			)}
			
		  </Box>
			{/* WYKRESY I ANALITYKA
	<Box textAlign="center" my={4}>
	  <Button
		component={NavLink}
		to={`/team/${teamId}/analytics`}
		variant="contained"
		color="primary"
		size="large"
		startIcon={<BarChartIcon />}
		sx={{
		  px: 5,
		  py: 1.2,
		  fontSize: '1.1rem',
		  fontWeight: 600,
		  borderRadius: 3,
		  boxShadow: 4,
		  '&:hover': { boxShadow: 8 },
		}}
	  >
		WYKRESY I ANALITYKA 
	  </Button>
	</Box>
	*/}
      {/* Ostatnie i najbliższe mecze */}
      <Grid container spacing={2} mb={3}>
        {/* Ostatnie mecze */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" component="h2" align="center" gutterBottom sx={{ mb: 2 }}>
			Ostatnie mecze
		  </Typography>
		  <TableContainer component={Paper} sx={{ bgcolor: '#1a1a1a', borderRadius: 2, maxWidth: '100%' }}>
			<Table size="small" sx={{ '& .MuiTableRow-root': { height: 48 } }}>
			  <TableHead>
				<TableRow sx={{ bgcolor: '#111' }}>
				  <TableCell sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.75rem' }, width: 80 }}>Termin</TableCell>
				  <TableCell align="right" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>Gospodarz</TableCell>
				  <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.75rem' }, width: 70 }}>Wynik</TableCell>
				  <TableCell sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>Gość</TableCell>
				  <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.75rem' }, width: 60 }}>±ELO</TableCell>
				</TableRow>
			  </TableHead>
			  <TableBody>
				{recentMatches.map((m, i) => {
				  const isHome = m.is_home;
				  const ourGoals = isHome ? m.home_goals : m.away_goals;
				  const oppGoals = isHome ? m.away_goals : m.home_goals;
				  const won = ourGoals > oppGoals;
				  const draw = ourGoals === oppGoals;
				  const resultColor = won ? '#4caf50' : draw ? '#ff9800' : '#f44336';
				  const eloChange = isHome ? m.elo_change :
								  -m.elo_change;
				  return (
					<TableRow key={m.match_id} sx={{ bgcolor: i % 2 === 0 ? '#222' : '#1a1a1a' }}>
					  {/* Data + kropka zaraz po dacie */}
					  <TableCell>
						<Box sx={{ display: 'flex', alignItems: 'center'}}>
						  <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.85rem' }, color: '#ccc', minWidth: 55 }}>
							{format(new Date(m.match_date), 'dd MMM', { locale: pl })}
						  </Typography>
						  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: resultColor }} />
						</Box>
					  </TableCell>
					  {/* Gospodarz */}
					  <TableCell align="right">
						<Typography component={NavLink}
						to={`/team/${m.home_team_id}/${slugify(m.home_name)}`} sx={{ fontWeight: isHome ? 700 : 400, color: 'white', fontSize: { xs: '0.7rem', md: '0.9rem' },
						textDecoration: 'none',
						cursor: 'pointer'
						}}>
						  {m.home_name}
						</Typography>
					  </TableCell>
					  {/* Wynik – teraz bez kropki, tylko biały tekst */}
					  <TableCell align="center">
						<Typography fontWeight={800} sx={{ color: 'white', fontSize: { xs: '0.8rem', md: '1rem' } }}>
						  {m.home_goals} : {m.away_goals}
						</Typography>
					  </TableCell>
					  {/* Gość */}
					  <TableCell>
						<Typography component={NavLink}
						to={`/team/${m.away_team_id}/${slugify(m.away_name)}`}sx={{ fontWeight: !isHome ? 700 : 400, color: 'white', fontSize: { xs: '0.7rem', md: '0.9rem' },
						textDecoration: 'none',
						cursor: 'pointer'}}>
						  {m.away_name}
						</Typography>
					  </TableCell>
					  {/* ±ELO */}
					  <TableCell align="center">
						<Typography
						  sx={{
							fontWeight: 600,
							fontSize: { xs: '0.75rem', md: '0.85rem' },
							color: eloChange > 0 ? '#4caf50' : eloChange < 0 ? '#f44336' : '#ff9800',
						  }}
						>
						  {eloChange > 0 ? '+' : ''}{eloChange}
						</Typography>
					  </TableCell>
					</TableRow>
				  );
				})}
			  </TableBody>
			</Table>
		  </TableContainer>
        </Grid>

        {/* Najbliższe mecze */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" component="h2" align="center" gutterBottom sx={{ mb: 2 }}>
			Najbliższe mecze
		  </Typography>
		  <TableContainer component={Paper} sx={{ bgcolor: '#1a1a1a', borderRadius: 2, maxWidth: '100%' }}>
			<Table size="small" sx={{ '& .MuiTableRow-root': { height: 48 } }}>
			  <TableHead>
				<TableRow sx={{ bgcolor: '#111' }}>
				  <TableCell sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.75rem' }, width: 120 }}>
					Termin
				  </TableCell>
				  <TableCell align="right" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
					Gospodarz
				  </TableCell>
				  <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.75rem' }, width: 70 }}>
				   
				  </TableCell>
				  <TableCell sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
					Gość
				  </TableCell>
				</TableRow>
			  </TableHead>
			  <TableBody>
				{upcomingMatches.slice(0, 10).map((m, i) => {
				  const isHome = m.is_home;
				  const dateStr = m.match_date
					? format(new Date(m.match_date), 'dd MMM yyyy', { locale: pl })
					: `Kolejka ${m.round}`;
				  const timeStr = m.match_date
					? format(new Date(m.match_date), 'HH:mm', { locale: pl })
					: null;
				  return (
					<TableRow key={m.match_id} sx={{ bgcolor: i % 2 === 0 ? '#222' : '#1a1a1a' }}>
					  {/* Data + godzina */}
					  <TableCell>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						  <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.85rem' }, color: '#ccc', minWidth: 70 }}>
							{dateStr}
						  </Typography>
						  {timeStr && (
							<Typography sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' }, color: '#00bcd4', fontWeight: 600 }}>
							  {timeStr}
							</Typography>
						  )}
						</Box>
					  </TableCell>
					  {/* Gospodarz */}
					  <TableCell align="right">
						<Typography component={NavLink}
						to={`/team/${m.home_team_id}/${slugify(m.home_name)}`} sx={{ fontWeight: isHome ? 700 : 400, color: 'white', fontSize: { xs: '0.7rem', md: '0.9rem' },
						textDecoration: 'none',
						cursor: 'pointer'				}}>
						  {m.home_name}
						</Typography>
					  </TableCell>
					  {/* vs */}
					  <TableCell align="center">
						<Typography sx={{ color: '#666', fontWeight: 600, fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
						  vs
						</Typography>
					  </TableCell>
					  {/* Gość */}
					  <TableCell>
						<Typography component={NavLink}
						to={`/team/${m.away_team_id}/${slugify(m.away_name)}`} sx={{ fontWeight: !isHome ? 700 : 400, color: 'white', fontSize: { xs: '0.7rem', md: '0.9rem' },
						textDecoration: 'none',
						cursor: 'pointer'}}>
						  {m.away_name}
						</Typography>
					  </TableCell>
					</TableRow>
				  );
				})}
			  </TableBody>
			</Table>
		  </TableContainer>
        </Grid>
      </Grid>
	  
{achievements.length > 0 && (
  <Box sx={{ my: 4, px: { xs: 0, md: 0 } }}>
    <Typography
      variant="h5"
      gutterBottom
      sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}
    >
      Wybryki statystyczne
    </Typography>

    <List sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, py: 1 }}>
      {achievements.map((ach) => {
        const rank = ach.rank;

        const getAchievementColor = () => {
          switch (ach.color) {
            case 'green': return '#4caf50';
            case 'red':   return '#f44336';
            case 'white': return '#ffffff';
            default:      return '#4caf50';
          }
        };
        const color = getAchievementColor();

        return (
          <ListItem key={ach.rankingUrl} sx={{ py: 0.8, alignItems: 'center' }}>
            {/* Kolumna 1 – ikona (puchar/medal) */}
            <Box sx={{ width: 10, display: 'flex', justifyContent: 'center' }}>
              {rank === 1 && <EmojiEventsIcon sx={{ color: '#ffd700', fontSize: 28 }} />}
              {rank === 2 && <span style={{ fontSize: '1.4rem' }}>🥈</span>}
              {rank === 3 && <span style={{ fontSize: '1.4rem' }}>🥉</span>}
            </Box>

            {/* Kolumna 2 – pozycja (#1, #12, #100 itp.) */}
            <Box sx={{ width: 50, textAlign: 'center' }}>
              <Typography
                component={NavLink}
                to={`/achievements/${ach.rankingUrl}`}
                sx={{
                  fontWeight: { xs: 700, md: 900 },
                  color: color,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                  fontSize: { xs: '1.0rem', md: '1.2rem' },
                }}
              >
                #{ach.rank}
              </Typography>
            </Box>

            {/* Kolumna 3 – opis (miejsce w kategorii + nazwa) */}
            <Box sx={{ flex: 1 }}>
              <Typography
                component="span"
                sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}
              >
                miejsce w kategorii: {' '}
                <Typography
                  component={NavLink}
                  to={`/achievements/${ach.rankingUrl}`}
                  sx={{
                    fontWeight: { xs: 700, md: 900 },
                    color: color,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {ach.displayName}
                </Typography>
              </Typography>
            </Box>
          </ListItem>
        );
      })}
    </List>
  </Box>
)}

      {/* Tabela ligowa */}
      {leagueTable.length > 0 && (
        <Box sx={{ maxWidth: { xs: '100%', md: 900 }, mx: 'auto' }}>
          <Typography variant="h5" align="center" gutterBottom sx={{ mb: 3 }}>
            {team.name} - tabela
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: '#1a1a1a', borderRadius: 2 }}>
            <Table size="dense" sx={{ '& .MuiTableRow-root': { height: 36 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#111' }}>
                  <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.65rem', md: '0.75rem' }, width: { xs: 25, md: 40 } }}>Poz.</TableCell>
                  <TableCell sx={{ color: '#aaa', fontSize: { xs: '0.65rem', md: '0.75rem' } }}>Drużyna</TableCell>
                  <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.65rem', md: '0.75rem' }, width: { xs: 20, md: 30 } }}>M</TableCell>
                  <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.65rem', md: '0.75rem' }, width: { xs: 40, md: 50 } }}>Pkt</TableCell>
                  <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.65rem', md: '0.75rem' }, width: { xs: 50, md: 70 } }}>Br</TableCell>
				  
					  <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.65rem', md: '0.75rem' }, width: 40 }}>H2H</TableCell>
					  <TableCell sx={{ color: '#aaa', fontSize: { xs: '0.65rem', md: '0.75rem' }, pl: 2 }}>Szansa pozycji końcowej</TableCell>
				  
                </TableRow>
              </TableHead>
              <TableBody>
                {leagueTable.map((row, index) => {
                  const isOurTeam = row.isOurTeam;
                  const probHigherThanUs = row.probHigherThanUs ?? 0;
                  const h2hChance = (1 - probHigherThanUs) * 100;

                  // Kolor H2H
                  const getH2hColor = (percent) => {
                    let r, g, b;
                    if (percent <= 50) {
                      const ratio = percent / 50;
                      r = 213 + (255 - 213) * ratio;
                      g = 0 + 152 * ratio;
                      b = 0;
                    } else {
                      const ratio = (percent - 50) / 50;
                      r = 255 - (255 - 76) * ratio;
                      g = 152 + (175 - 152) * ratio;
                      b = 0 + 80 * ratio;
                    }
                    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
                  };
                  const h2hColor = getH2hColor(h2hChance);

                  // 5. Poprawka: szansa na pozycję końcową – teraz działa!
                  const posProbObj = positionProbabilities?.find(p => p.position === index + 1);
				  const posProb = posProbObj?.probability ?? 0;

                  return (
                    <TableRow key={row.team_id} sx={{ bgcolor: isOurTeam ? 'rgba(76, 175, 80, 0.15)' : 'transparent' }}>
                      {/* 6. Pozycja – teraz kolejna, nie z API */}
                      <TableCell align="center">
                        <Typography fontWeight={700} color={isOurTeam ? '#4caf50' : 'inherit'} sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                          {index + 1}.
                        </Typography>
                      </TableCell>

                      {/* Drużyna */}
                      <TableCell>
                        <Typography component={NavLink} to={`/team/${row.team_id}/${slugify(row.name)}`} fontWeight={isOurTeam ? 800 : 500} color="white" sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                          {row.name}
                        </Typography>
                      </TableCell>

                      {/* M */}
                    <TableCell align="center">
                      <Typography fontWeight={600} color="#ccc" sx={{ fontSize: { xs: '0.6rem', md: '0.85rem' } }}>
                        {row.matches_played ?? '-'}
                      </Typography>
                    </TableCell>

                    {/* Pkt */}
                    <TableCell align="center">
                      <Typography fontWeight={600} sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' } }}>{row.points}</Typography>
                    </TableCell>

                    {/* Bramki */}
                    <TableCell align="center">
                      <Typography fontWeight={500} sx={{ fontSize: { xs: '0.7rem', md: '0.85rem' } }}>
                        {row.goals_scored}:{row.goals_conceded}
                      </Typography>
                    </TableCell>
					

						{/* H2H – większa czcionka + soczysty gradient */}
						<TableCell align="center">
						
						  {isOurTeam ? (
							<Typography color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' } }}>—</Typography>
						  ) : (
							<Box sx={{ width: {xs: '100%', md: 78}, height: {xs: 22, md: 30}, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)', position: 'relative', overflow: 'hidden' }}>
														<RequireVip variant='short-text' requirement={team.statsRequirement}>

							  <Box sx={{ width: `${h2hChance}%`, height: '100%', bgcolor: h2hColor, borderRadius: 1.5 }} />
							  <Typography
								sx={{
								  position: 'absolute',
								  top: '50%',
								  left: '50%',
								  transform: 'translate(-50%, -50%)',
								  color: 'white',
								  fontWeight: 900,
								  fontSize: { xs: '0.7rem', md: '0.9rem' },
								  textShadow: '0 0 4px rgba(0,0,0,0.6)',
								}}
							  >
								{h2hChance.toFixed(0)}%
							  </Typography>
							  </RequireVip>
							</Box>
														


						  )}
						</TableCell>

						{/* Szansa pozycji końcowej – większa czcionka + ciasno */}
						<TableCell>
						  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.0, pl: 1 }}>
							<Box sx={{ width: {xs: '100%', md: '100%'}, height: {xs: 22, md: 30}, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 1.5, position: 'relative' }}>
																					<RequireVip variant='nothing' requirement={team.statsRequirement}>

							  <Box sx={{ width: `${posProb}%`, height: '100%', bgcolor: 'white', borderRadius: 1.5, transition: 'width 1.0s ease' }} />
							  <Typography
								sx={{
								  position: 'absolute',
								  top: '50%',
								  left: posProb >= 60 ? '10px' : '70%',
								  transform: posProb >= 60 ? 'translateY(-50%)' : 'translate(-50%, -50%)',
								  color: posProb >= 60 ? '#111' : 'white',
								  fontWeight: 900,
								  fontSize: { xs: '0.65rem', md: '0.9rem' },
								  textShadow: posProb >= 35 ? 'none' : '0 0 4px rgba(0,0,0,0.6)',
								}}
							  >
								{posProb.toFixed(1)}%
							  </Typography>
							  </RequireVip>
							</Box>
						  </Box>
						</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
		  <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 1000, color: 'text.secondary', mt: 2 }}>
			Tabela widziana z perspektywy zaznaczonej drużyny
		  </Typography>
		  <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 1000, color: 'text.secondary'}}>
			H2H - szansa na zakończenie sezonu wyżej, niż drużyna z tego wiersza
		  </Typography>
		  <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 1000, color: 'text.secondary'}}>
		    Szansa pozycji końcowej - szansa na zajęcie miejsca z tego wiersza przez zaznaczoną drużynę
		  </Typography>
        </Box>
      )}

      {/* Przyciski 
      <Box textAlign="center" my={3}>
        <Button component={NavLink} to={`/team/${teamId}/elo-history`} variant="contained" size="large" startIcon={<TimelineIcon />} sx={{ mx: 2, mb: 2, px: 4 }}>
          Wykres historii ELO
        </Button>
        <Button component={NavLink} to={`/team/${teamId}/position-simulation`} variant="contained" size="large" startIcon={<BarChartIcon />} sx={{ mx: 2, mb: 2, px: 4 }}>
          Szanse na pozycje w lidze
        </Button>
      </Box>  */}
    </Box>
	</>
  );
};

export default TeamPage;
